/**
 * Copyright: ThoughtSpot Inc. 2024
 * Author: Mohamed Abdulla (mohamed.abdulla@thoughtspot.com)
 *
 * @fileoverview
 * Image component which validates the source URL using a network request.
 * If the source URL is invalid, it uses the fallbackSrc if provided.
 */
import { create } from '@thoughtspot/logger';
import React, { useEffect, useRef, useState } from 'react';
import { existingImageUrls } from './image.util';
const logger = create('image-component');
/**
 * @param src The source URL of the image.
 * @param fallbackSrc The URL of the fallback image.
 * @param props Additional props to be passed to the underlying <img /> element.
 * @returns A React element of type <img />.
 */
export const Image = ({ src, onImageLoadError, fallbackSrc, ...props }) => {
    const [currentSrc, setCurrentSrc] = useState(fallbackSrc);
    const isMounted = useRef(false);
    const prevSrc = useRef(src);
    const currentBlobUrl = useRef(null);
    // Handle mounting and cleanup
    useEffect(() => {
        isMounted.current = true;
        return () => {
            // Cleanup blob URL on unmount
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            // This cleanup is required to handle the updates correctly
            if (prevSrc.current) {
                existingImageUrls.delete(prevSrc.current);
            }
            isMounted.current = false;
        };
    }, []);
    // Track src changes
    useEffect(() => {
        prevSrc.current = src;
    }, [src]);
    // Handle image loading
    useEffect(() => {
        const fetchImage = async () => {
            // Cleanup previous blob URL before setting new one
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            let imgSrc = fallbackSrc;
            let blob;
            try {
                if (src) {
                    // Check cache first
                    if (existingImageUrls.has(src)) {
                        const cachedUrl = await existingImageUrls.get(src);
                        blob = cachedUrl;
                    }
                    else {
                        // Create and store the promise
                        const getBlobUrl = async (src) => {
                            try {
                                const response = await fetch(src);
                                return response.ok
                                    ? await response.blob()
                                    : null;
                            }
                            catch (_a) {
                                return null;
                            }
                        };
                        const blobUrlPromise = getBlobUrl(src);
                        if (blobUrlPromise) {
                            existingImageUrls.set(src, blobUrlPromise);
                        }
                        blob = await blobUrlPromise;
                    }
                    if (blob instanceof Blob && blob.size > 0) {
                        const blobUrl = URL.createObjectURL(blob);
                        currentBlobUrl.current = blobUrl;
                        imgSrc = blobUrl;
                    }
                }
            }
            catch (err) {
                logger.warn(err);
                imgSrc = fallbackSrc;
            }
            if (isMounted.current) {
                if (!imgSrc && onImageLoadError) {
                    onImageLoadError();
                    return;
                }
                setCurrentSrc(imgSrc);
            }
        };
        if (isMounted.current) {
            fetchImage();
        }
    }, [src, fallbackSrc, onImageLoadError]);
    return React.createElement("img", { ...props, src: currentSrc, alt: props.alt });
};
//# sourceMappingURL=image.js.map